import { useState, useEffect, useRef } from 'react';
import { json, redirect } from '@remix-run/node';
import { Form, useActionData, useSearchParams, useNavigation, Link } from '@remix-run/react';
import { motion } from 'framer-motion';
import { getLoggedInUser, login } from '../services/auth.server';
import { Heart, Sparkles, ArrowRight, Mail, CheckCircle, RefreshCw } from 'lucide-react';

export const loader = async ({ request }) => {
  // If user is already logged in, redirect to home
  const user = await getLoggedInUser(request);
  if (user) return redirect('/');
  return null;
};

export const action = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get('email')?.trim();
  const redirectTo = formData.get('redirectTo') || '/pathology-analysis';
  
  if (!email || typeof email !== 'string') {
    return json({ error: 'Please enter a valid email address' }, { status: 400 });
  }
  
  if (!email.includes('@')) {
    return json({ error: 'Please enter a valid email address' }, { status: 400 });
  }
  
  try {
    // Send magic link email with patient userType
    await login({ email, userType: 'patient' }, redirectTo);
    return json({ success: true, email });
  } catch (error) {
    console.error('Login error:', error);
    return json({ error: 'An error occurred during login. Please try again.' }, { status: 500 });
  }
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

export default function LoginPage() {
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const actionData = useActionData();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const redirectTo = searchParams.get('redirectTo') || '/pathology-analysis';
  const isSubmitting = navigation.state === 'submitting';
  const cooldownIntervalRef = useRef(null);
  
  // Set emailSent to true when the action returns success
  useEffect(() => {
    if (actionData?.success && !emailSent) {
      setEmailSent(true);
      setSentEmail(actionData.email || '');
      // Start 60 second cooldown
      setResendCooldown(60);
    }
  }, [actionData?.success, emailSent]);
  
  // Handle cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [resendCooldown]);
  
  // Handle resend
  const handleResend = async (e) => {
    e.preventDefault();
    if (resendCooldown > 0 || isResending) return;
    
    setIsResending(true);
    const form = e.target.closest('form') || e.target.form;
    if (form) {
      form.requestSubmit();
    }
    setIsResending(false);
    // Reset cooldown
    setResendCooldown(60);
  };
  
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-800 font-sans selection:bg-stone-200 relative overflow-hidden flex flex-col">
      
      {/* Decorative organic shapes */}
      <div className="fixed top-0 left-0 w-[800px] h-[800px] bg-[#E8F0E9] rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3 opacity-70 pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-[#FFF4E0] rounded-full blur-3xl translate-x-1/3 translate-y-1/3 opacity-70 pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-6 flex justify-between items-center max-w-6xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#D97777] rounded-full flex items-center justify-center text-white">
            <Heart className="w-4 h-4 fill-current" />
          </div>
          <span className="font-serif text-xl font-medium tracking-tight">HealthQ</span>
        </Link>
        <Link to="/" className="text-stone-500 hover:text-stone-800 font-medium text-sm transition-colors">
          Back to home
        </Link>
      </nav>

      <main className="flex-1 flex flex-col justify-center relative z-10 px-6 py-12">
        <div className="max-w-md mx-auto w-full">
          
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm text-stone-500 text-sm mb-6 border border-stone-100"
            >
              <Sparkles className="w-4 h-4 text-[#D97777]" />
              <span>Secure Magic Link Login</span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="text-4xl md:text-5xl font-serif text-stone-900 mb-4 leading-tight"
            >
              Welcome back
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              transition={{ delay: 0.2 }}
              className="text-lg text-stone-500 font-light leading-relaxed"
            >
              Enter your email to receive a secure magic link
            </motion.p>
          </motion.div>

          {/* Login Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-stone-100"
          >
            {emailSent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#E8F0E9] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#5B8C65]" />
                </div>
                <h3 className="text-xl font-serif text-stone-800 mb-3">Check your inbox</h3>
                <p className="text-stone-500 mb-2 leading-relaxed">
                  We've sent a magic link to:
                </p>
                <p className="text-[#5B8C65] font-medium mb-6">
                  {sentEmail || actionData?.email}
                </p>
                <p className="text-sm text-stone-400 mb-6">
                  Click the link in the email to sign in securely.
                </p>
                
                {/* Resend Magic Link */}
                <div className="pt-6 border-t border-stone-100 space-y-3">
                  <Form method="post" onSubmit={handleResend}>
                    <input type="hidden" name="email" value={sentEmail || actionData?.email || ''} />
                    <input type="hidden" name="redirectTo" value={redirectTo} />
                    <button
                      type="submit"
                      disabled={resendCooldown > 0 || isResending || isSubmitting}
                      className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium transition-all ${
                        resendCooldown > 0 || isResending || isSubmitting
                          ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
                          : 'bg-white text-[#5B8C65] border-2 border-[#5B8C65] hover:bg-[#E8F0E9] shadow-sm hover:shadow-md'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5B8C65]/50`}
                    >
                      {isResending || isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-[#5B8C65] border-t-transparent rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : resendCooldown > 0 ? (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          <span>Resend in {resendCooldown}s</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          <span>Resend magic link</span>
                        </>
                      )}
                    </button>
                  </Form>
                  
                  {/* Change Email Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setEmailSent(false);
                      setSentEmail('');
                      setResendCooldown(0);
                    }}
                    className="w-full text-sm text-stone-500 hover:text-stone-700 font-medium transition-colors"
                  >
                    Use a different email
                  </button>
                </div>
              </motion.div>
            ) : (
              <Form method="post" className="space-y-6">
                <input type="hidden" name="redirectTo" value={redirectTo} />
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full pl-12 pr-4 py-3 border border-stone-200 rounded-xl text-stone-900 bg-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#5B8C65]/20 focus:border-[#5B8C65] transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                
                {actionData?.error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl bg-[#FFF5F5] border border-[#FFE4E1] p-4 text-sm text-[#D97777]"
                  >
                    {actionData.error}
                  </motion.div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`group relative w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-medium transition-all ${
                    isSubmitting 
                      ? 'bg-stone-400 cursor-not-allowed' 
                      : 'bg-[#D97777] hover:bg-[#C85C5C] shadow-md hover:shadow-lg'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D97777]/50`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send magic link</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </Form>
            )}
          </motion.div>

          {/* Footer text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-sm text-stone-400 mt-6"
          >
            Don't have an account?{' '}
            <Link to="/" className="text-[#5B8C65] hover:text-[#4A7A54] font-medium transition-colors">
              Get started
            </Link>
          </motion.p>
        </div>
      </main>
    </div>
  );
}
